export type UploadMediaMode = "document" | "video";

/** PDF uploads — align with typical document multipart limits. */
export const RAG_UPLOAD_MAX_FILE_BYTES_DOCUMENT = 10 * 1024 * 1024;

/** Video uploads — same per-file cap as documents (10 MB). */
export const RAG_UPLOAD_MAX_FILE_BYTES_VIDEO = 10 * 1024 * 1024;

export function uploadMaxFileBytes(mode: UploadMediaMode): number {
  return mode === "video"
    ? RAG_UPLOAD_MAX_FILE_BYTES_VIDEO
    : RAG_UPLOAD_MAX_FILE_BYTES_DOCUMENT;
}

/** @deprecated Prefer uploadMaxFileBytes(mode); kept for callers expecting a flat 10 MB doc limit. */
export const MAX_RAG_UPLOAD_FILE_SIZE_MB = 10;
export const MAX_RAG_UPLOAD_FILE_SIZE_BYTES = RAG_UPLOAD_MAX_FILE_BYTES_DOCUMENT;

export const RAG_UPLOAD_FILE_SIZE_EXCEEDED_MESSAGE =
  "The maximum file size has been exceeded. Please upload a file that is 10 MB or smaller.";

export const RAG_UPLOAD_FILE_SIZE_EXCEEDED_MESSAGE_VIDEO =
  "File exceeds 10 MB limit";

/** Matches backend multipart max-request-size (per media mode). */
export function uploadMaxTotalBytes(mode: UploadMediaMode): number {
  return uploadMaxFileBytes(mode);
}

export const MAX_RAG_UPLOAD_TOTAL_SIZE_BYTES = MAX_RAG_UPLOAD_FILE_SIZE_BYTES;

export const RAG_UPLOAD_TOTAL_SIZE_EXCEEDED_MESSAGE =
  "The total size of selected files exceeds 10 MB. Remove some files or upload fewer at a time.";

export const RAG_UPLOAD_TOTAL_SIZE_EXCEEDED_MESSAGE_VIDEO =
  "The total size of selected files exceeds 10 MB. Remove some files or upload fewer at a time.";

export const RAG_UPLOAD_SIZE_ERROR_KEYS = {
  perFileDocument: "opva.dmUploadFileTooLargeDocument",
  perFileVideo: "opva.dmUploadFileTooLargeVideo",
  /** Legacy key used by older RAG deploy strings (documents). */
  perFile: "opva.dmUploadFileSizeExceeded",
  total: "opva.dmUploadTotalSizeExceeded",
  totalVideo: "opva.dmUploadTotalSizeExceededVideo",
} as const;

export type UploadSizeErrorKey =
  (typeof RAG_UPLOAD_SIZE_ERROR_KEYS)[keyof typeof RAG_UPLOAD_SIZE_ERROR_KEYS];

export type UploadSizeError = {
  message: string;
  key: UploadSizeErrorKey;
};

const VIDEO_EXTENSIONS = new Set([
  ".mp4",
  ".mov",
  ".avi",
  ".mkv",
  ".webm",
  ".m4v",
]);

/** Junk / system files to ignore when bulk-selecting a folder for RAG upload. */
const SKIP_BASENAMES_LOWER = new Set([
  ".ds_store",
  "thumbs.db",
  "desktop.ini",
]);

export type UploadFileNormalizationResult = {
  files: File[];
  skipped: string[];
  duplicateError: string | null;
  sizeError: string | null;
  sizeErrorKey: UploadSizeErrorKey | null;
};

export function getTotalUploadSizeBytes(files: File[]): number {
  return files.reduce((total, file) => total + file.size, 0);
}

export function isUploadBatchWithinTotalSizeLimit(
  files: File[],
  mode: UploadMediaMode = "document"
): boolean {
  return getTotalUploadSizeBytes(files) <= uploadMaxTotalBytes(mode);
}

export function isUploadFileWithinSizeLimit(
  file: File,
  mode: UploadMediaMode = "document"
): boolean {
  return file.size <= uploadMaxFileBytes(mode);
}

export function getUploadFileSizeError(
  files: File[],
  mode: UploadMediaMode = "document"
): UploadSizeError | null {
  const oversized = files.filter(
    (file) => !isUploadFileWithinSizeLimit(file, mode)
  );
  if (oversized.length > 0) {
    if (mode === "video") {
      return {
        message: RAG_UPLOAD_FILE_SIZE_EXCEEDED_MESSAGE_VIDEO,
        key: RAG_UPLOAD_SIZE_ERROR_KEYS.perFileVideo,
      };
    }
    return {
      message: RAG_UPLOAD_FILE_SIZE_EXCEEDED_MESSAGE,
      key: RAG_UPLOAD_SIZE_ERROR_KEYS.perFile,
    };
  }
  if (!isUploadBatchWithinTotalSizeLimit(files, mode)) {
    if (mode === "video") {
      return {
        message: RAG_UPLOAD_TOTAL_SIZE_EXCEEDED_MESSAGE_VIDEO,
        key: RAG_UPLOAD_SIZE_ERROR_KEYS.totalVideo,
      };
    }
    return {
      message: RAG_UPLOAD_TOTAL_SIZE_EXCEEDED_MESSAGE,
      key: RAG_UPLOAD_SIZE_ERROR_KEYS.total,
    };
  }
  return null;
}

/** Basename only — strips folder segments from directory uploads. */
export function uploadBasename(file: File): string {
  const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  const raw = (rel && rel.trim()) || file.name || "upload.bin";
  const normalized = raw.replace(/\\/g, "/").replace(/^\/+/, "");
  const parts = normalized.split("/").filter(Boolean);
  const base = parts.length > 0 ? parts[parts.length - 1]! : normalized;
  const trimmed = base.trim();
  return trimmed || "upload.bin";
}

/** Human-readable source path (may include subfolders) for errors and tooltips. */
export function uploadSourceLabel(file: File): string {
  const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  return (rel && rel.trim()) || file.name || uploadBasename(file);
}

export function fileExtensionLower(file: File): string {
  const base = uploadBasename(file);
  const dot = base.lastIndexOf(".");
  if (dot < 0) return "";
  return base.slice(dot).toLowerCase();
}

export function isVideoUploadFile(file: File): boolean {
  const ext = fileExtensionLower(file);
  if (VIDEO_EXTENSIONS.has(ext)) return true;
  const mime = (file.type || "").toLowerCase();
  return mime.startsWith("video/");
}

export function isDocumentUploadFile(file: File): boolean {
  const ext = fileExtensionLower(file);
  return ext === ".pdf" || (file.type || "").toLowerCase() === "application/pdf";
}

export function filterFilesForUploadMode(
  incoming: File[],
  mode: UploadMediaMode
): { accepted: File[]; rejected: string[] } {
  const accepted: File[] = [];
  const rejected: string[] = [];
  for (const file of incoming) {
    const source = uploadSourceLabel(file);
    if (shouldSkipUploadFile(file)) {
      continue;
    }
    const ok =
      mode === "video" ? isVideoUploadFile(file) : isDocumentUploadFile(file);
    if (ok) {
      accepted.push(file);
    } else {
      rejected.push(source);
    }
  }
  return { accepted, rejected };
}

export function uploadFileAcceptAttribute(mode: UploadMediaMode): string {
  return mode === "video"
    ? "video/*,.mp4,.mov,.avi,.mkv,.webm,.m4v"
    : ".pdf,application/pdf";
}

export function filterFilesByMaxSize(
  incoming: File[],
  mode: UploadMediaMode
): {
  accepted: File[];
  oversized: string[];
} {
  const maxBytes = uploadMaxFileBytes(mode);
  const accepted: File[] = [];
  const oversized: string[] = [];
  for (const file of incoming) {
    if (file.size <= maxBytes) {
      accepted.push(file);
    } else {
      oversized.push(uploadBasename(file));
    }
  }
  return { accepted, oversized };
}

export function shouldSkipUploadFile(file: File): boolean {
  const base = uploadBasename(file);
  const lower = base.toLowerCase();
  if (SKIP_BASENAMES_LOWER.has(lower)) return true;
  if (lower.startsWith("~$")) return true;
  if (base.startsWith(".")) return true;
  return file.size === 0;
}

/**
 * Prepare a file list for RAG upload: skip junk, flatten to basenames, reject duplicates,
 * and enforce per-file / batch size limits for the active media mode.
 */
export function normalizeUploadFileSelection(
  incoming: File[],
  mode: UploadMediaMode = "document"
): UploadFileNormalizationResult {
  const skipped: string[] = [];
  const byBasename = new Map<
    string,
    { file: File; sources: string[]; displayName: string }
  >();

  for (const file of incoming) {
    const source = uploadSourceLabel(file);
    if (shouldSkipUploadFile(file)) {
      skipped.push(source);
      continue;
    }
    const displayName = uploadBasename(file);
    const key = displayName.toLowerCase();
    const existing = byBasename.get(key);
    if (existing) {
      existing.sources.push(source);
    } else {
      byBasename.set(key, { file, sources: [source], displayName });
    }
  }

  const duplicateLines: string[] = [];
  Array.from(byBasename.values()).forEach((entry) => {
    if (entry.sources.length > 1) {
      duplicateLines.push(
        `${entry.displayName} (${entry.sources.join("; ")})`
      );
    }
  });

  const duplicateError =
    duplicateLines.length > 0
      ? `Duplicate file names in this selection (remove one copy of each): ${duplicateLines.join(
          " · "
        )}`
      : null;

  const candidateFiles = duplicateError
    ? []
    : Array.from(byBasename.values()).map((e) => e.file);
  const sizeErr = duplicateError
    ? null
    : getUploadFileSizeError(candidateFiles, mode);
  const sizeError = sizeErr?.message ?? null;
  const sizeErrorKey = sizeErr?.key ?? null;
  const files = sizeError ? [] : candidateFiles;

  return { files, skipped, duplicateError, sizeError, sizeErrorKey };
}
